import React from "react";

// ✅ Schema: section name → fixed rows + column mapping
export const sectionsSchema = {
  organolepticProperties: {
    title: "Organoleptic Properties",
    rows: ["Color", "Appearance", "Aroma", "Moisture", "Taste", "Solubility", "Bulk Density", "Sieve"],
    columns: [
      ["property", "Parameter"],
      ["specification", "Specification"],
      ["method", "Method"],
    ],
  },
  heavyMetals: {
    title: "Heavy Metals",
    rows: ["Arsenic", "Mercury", "Lead", "Cadmium"],
    columns: [
      ["metal", "Parameter"],
      ["specification", "Specification"],
      ["method", "Method"],
    ],
  },
  microbiology: {
    title: "Microbiology",
    rows: [
      "Total Plate Count",
      "Yeast & Mold",
      "Coli Forms",
      "Salmonella",
      "Staphylococcus aureus",
      "Pseudomonas spp",
      "E.coli",
      "Clostridium spp",
    ],
    columns: [
      ["test", "Parameter"],
      ["specification", "Specification"],
      ["method", "Method"],
    ],
  },
  nutritionalInformation: {
    title: "Nutritional Information",
    rows: ["Energy", "Carbohydrate", "Sugar", "Fat", "Protein", "Active Ingredient"],
    columns: [
      ["type", "Parameter"],
      ["specification", "Specification"],
      ["method", "Method"],
    ],
  },
  allergenInformation: {
    title: "Allergen Information",
    rows: [
      "Peanut or its derivatives",
      "Tree Nuts (almonds, cashews, etc.)",
      "Coconut or its derivatives",
      "Sesame, Cotton Seed or its derivatives",
      "Dairy Products or its derivatives",
      "Egg Products or its derivatives",
      "Fish/shellfish or its derivatives",
      "Soy or its derivatives",
      "Corn or its derivatives",
      "Mustard or its derivatives",
      "Celery / Other umbelliferae",
      "Cereals containing Gluten",
      "Crustaceans/shellfish",
      "SO2 or Sulphite >10mg/kg or 10mg/L",
      "Molluscs or its derivatives",
      "Lupins or its derivatives",
      "Lentinan",
    ],
    columns: [
      ["allergen", "Allergen Type"],
      ["inProduct", "InProduct?"],
      ["inOtherProducts", "In other Products?"],
      ["inPlant", "InPlant?"],
    ],
  },
};

// ✅ Radio button group for allergens
const RadioGroup = ({ value, onChange }) => {
  const options = ["Yes", "No"];
  return (
    <div className="btn-group btn-group-sm" role="group">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`btn ${value === opt ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => onChange(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
};

// ✅ Table Section Component
const TableSection = ({ fieldKey, schema, data, onChange }) => {
  const sectionData = data || [];

  const handleChange = (rowName, columnKey, value) => {
    const updated = [...sectionData];
    const idx = updated.findIndex((row) => row[schema.columns[0][0]] === rowName);
    if (idx >= 0) {
      updated[idx] = { ...updated[idx], [columnKey]: value };
    } else {
      updated.push({ [schema.columns[0][0]]: rowName, [columnKey]: value });
    }
    onChange(fieldKey, updated);
  };

  const getValue = (rowName, columnKey) => {
    const row = sectionData.find((r) => r[schema.columns[0][0]] === rowName);
    return row ? row[columnKey] || "" : "";
  };

  return (
    <div className="mb-4">
      <h5 className="text-success">{schema.title}</h5>
      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            {schema.columns.map(([_, label], idx) => (
              <th key={idx}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {schema.rows.map((rowName, idx) => (
            <tr key={idx}>
              {/* First column is fixed label */}
              <td>{rowName}</td>
              {/* Other columns: radio buttons if allergen section, otherwise text inputs */}
              {schema.columns.slice(1).map(([colKey]) => (
                <td key={colKey}>
                  {fieldKey === "allergenInformation" ? (
                    <RadioGroup
                      value={getValue(rowName, colKey)}
                      onChange={(val) => handleChange(rowName, colKey, val)}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={getValue(rowName, colKey)}
                      onChange={(e) => handleChange(rowName, colKey, e.target.value)}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Main Form
export default function ProductSpecificationForm({ data, onChange, onNext, onBack }) {
  return (
    <div>
      <h4 className="text-success">Product Specification</h4>

      {/* Basic fields */}
      <div className="row">
        <div className="col-md-3 mb-3">
          <label htmlFor="extractionType">Extraction Type</label>
          <input
            id="extractionType"
            type="text"
            className="form-control"
            value={data.extractionType ?? ""}
            onChange={(e) => onChange("extractionType", e.target.value)}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="ratio">Ratio</label>
          <input
            id="ratio"
            type="text"
            className="form-control"
            value={data.ratio ?? ""}
            onChange={(e) => onChange("ratio", e.target.value)}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="drying">Drying</label>
          <input
            id="drying"
            type="text"
            className="form-control"
            value={data.drying ?? ""}
            onChange={(e) => onChange("drying", e.target.value)}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="excipients">Excipients</label>
          <input
            id="excipients"
            type="text"
            className="form-control"
            value={data.excipients ?? ""}
            onChange={(e) => onChange("excipients", e.target.value)}
          />
        </div>
      </div>

      {/* Table Sections */}
      {Object.entries(sectionsSchema).map(([key, schema]) => (
        <TableSection
          key={key}
          fieldKey={key}
          schema={schema}
          data={data[key] || []}
          onChange={(sectionKey, updated) => onChange(sectionKey, updated)}
        />
      ))}

      <div className="d-flex justify-content-between">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  );
}
